import { generateCodeVerifier, createCodeChallenge } from './pkce'

export type DiscordUser = {
  id: string
  username: string
  global_name?: string
  avatar?: string | null
  discriminator?: string
}

type StoredAuth = {
  accessToken: string
  refreshToken?: string
  tokenType: string
  expiresAt: number
  scope: string
  user: DiscordUser
}

const STORAGE_KEY = 'auth.discord'
const CODE_VERIFIER_KEY = 'auth.discord.code_verifier'
const REDIRECT_PATH = '/auth/callback'

// Configure these via Vite env variables
const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string || '1234567890123456789' // Fallback for testing

// Get redirect URI based on environment
const getRedirectUri = () => {
  console.log('getRedirectUri called')
  console.log('VITE_DISCORD_REDIRECT_URI:', import.meta.env.VITE_DISCORD_REDIRECT_URI)
  console.log('DEV:', import.meta.env.DEV)
  console.log('PROD:', import.meta.env.PROD)
  console.log('MODE:', import.meta.env.MODE)
  
  // If explicitly set in env, use it
  if (import.meta.env.VITE_DISCORD_REDIRECT_URI) {
    console.log('Using explicit redirect URI from env')
    return import.meta.env.VITE_DISCORD_REDIRECT_URI as string
  }
  
  // Otherwise, determine based on environment
  if (import.meta.env.DEV) {
    console.log('Using development redirect URI')
    return `http://localhost:3001${REDIRECT_PATH}`
  } else {
    console.log('Using production redirect URI')
    return `https://hugoinc.family${REDIRECT_PATH}`
  }
}
const scopes = (import.meta.env.VITE_DISCORD_SCOPES as string) || 'identify'
const flow = (import.meta.env.VITE_DISCORD_FLOW as string) || 'pkce' // 'pkce' | 'implicit'

export const getStoredAuth = (): StoredAuth | null => {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed: StoredAuth = JSON.parse(raw)
    return parsed
  } catch {
    return null
  }
}

export const clearStoredAuth = () => {
  localStorage.removeItem(STORAGE_KEY)
}

export const isAuthenticated = (): boolean => {
  const auth = getStoredAuth()
  if (!auth) return false
  return Date.now() < auth.expiresAt - 5000
}

export const getUser = (): DiscordUser | null => {
  const auth = getStoredAuth()
  return auth?.user || null
}

export const loginWithDiscord = async () => {
  const redirectUri = getRedirectUri()
  
  console.log('loginWithDiscord called')
  console.log('Environment:', import.meta.env.MODE)
  console.log('DEV mode:', import.meta.env.DEV)
  console.log('PROD mode:', import.meta.env.PROD)
  console.log('Client ID:', clientId)
  console.log('Redirect URI:', redirectUri)
  console.log('Scopes:', scopes)
  console.log('Flow:', flow)
  
  // Check if client ID is properly configured
  if (!clientId || clientId === '1234567890123456789') {
    alert('Discord OAuth не настроен. Пожалуйста, создайте Discord Application и добавьте VITE_DISCORD_CLIENT_ID в .env.local файл.')
    return
  }

  const authorizeUrl = new URL('https://discord.com/oauth2/authorize')
  authorizeUrl.searchParams.set('client_id', clientId)
  authorizeUrl.searchParams.set('scope', scopes)
  authorizeUrl.searchParams.set('redirect_uri', redirectUri)

  if (flow === 'implicit') {
    authorizeUrl.searchParams.set('response_type', 'token')
  } else {
    const codeVerifier = generateCodeVerifier(64)
    localStorage.setItem(CODE_VERIFIER_KEY, codeVerifier)
    const codeChallenge = await createCodeChallenge(codeVerifier)
    authorizeUrl.searchParams.set('response_type', 'code')
    authorizeUrl.searchParams.set('code_challenge', codeChallenge)
    authorizeUrl.searchParams.set('code_challenge_method', 'S256')
    
    console.log('Code verifier saved:', codeVerifier ? 'yes' : 'no')
    console.log('Code challenge generated:', codeChallenge ? 'yes' : 'no')
  }

  console.log('Final Discord OAuth URL:', authorizeUrl.toString())
  console.log('Redirecting to Discord...')
  window.location.href = authorizeUrl.toString()
}

const parseHash = (): Record<string, string> => {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : ''
  const params = new URLSearchParams(hash)
  const out: Record<string, string> = {}
  params.forEach((v, k) => { out[k] = v })
  return out
}

export const handleDiscordCallback = async (): Promise<void> => {
  console.log('handleDiscordCallback started')
  console.log('Flow:', flow)
  
  // Implicit flow: access_token comes in hash fragment
  if (flow === 'implicit') {
    console.log('Using implicit flow')
    const hash = parseHash()
    if (hash.error) throw new Error(hash.error)
    const accessToken = hash.access_token
    const tokenType = hash.token_type
    const expiresIn = Number(hash.expires_in || '0')
    if (!accessToken || !tokenType) throw new Error('Missing access token')
    // Fetch user
    const meResp = await fetch('https://discord.com/api/users/@me', {
      headers: { Authorization: `${tokenType} ${accessToken}` },
    })
    if (!meResp.ok) throw new Error('Failed to fetch user')
    const user = (await meResp.json()) as DiscordUser
    const stored: StoredAuth = {
      accessToken,
      tokenType,
      expiresAt: Date.now() + (expiresIn || 3600) * 1000,
      scope: scopes,
      refreshToken: undefined,
      user,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    return
  }

  console.log('Using PKCE flow')
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const error = params.get('error')
  console.log('Code:', code ? 'present' : 'missing')
  console.log('Error:', error)
  
  if (error) throw new Error(error)
  if (!code) throw new Error('Missing authorization code')

  const codeVerifier = localStorage.getItem(CODE_VERIFIER_KEY)
  console.log('Code verifier:', codeVerifier ? 'present' : 'missing')
  console.log('Current domain:', window.location.origin)
  console.log('Expected redirect URI:', getRedirectUri())
  
  if (!codeVerifier) {
    console.error('Missing code_verifier - this usually means redirect URI mismatch')
    console.error('Current URL:', window.location.href)
    throw new Error('Missing code_verifier - redirect URI mismatch detected')
  }
  localStorage.removeItem(CODE_VERIFIER_KEY)

  // Exchange code for token directly with Discord
  const tokenUrl = 'https://discord.com/api/oauth2/token'
  const body = new URLSearchParams()
  body.set('client_id', clientId)
  body.set('grant_type', 'authorization_code')
  body.set('code', code)
  body.set('redirect_uri', getRedirectUri())
  body.set('code_verifier', codeVerifier)

  const tokenResp = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })

  if (!tokenResp.ok) {
    throw new Error('Failed to exchange code')
  }
  const tokenJson = await tokenResp.json()
  const accessToken = tokenJson.access_token as string
  const tokenType = tokenJson.token_type as string
  const expiresIn = tokenJson.expires_in as number
  const scope = tokenJson.scope as string
  const refreshToken = tokenJson.refresh_token as string | undefined

  // Fetch user info
  const meResp = await fetch('https://discord.com/api/users/@me', {
    headers: { Authorization: `${tokenType} ${accessToken}` },
  })
  if (!meResp.ok) {
    throw new Error('Failed to fetch user')
  }
  const user = (await meResp.json()) as DiscordUser

  const stored: StoredAuth = {
    accessToken,
    refreshToken,
    tokenType,
    expiresAt: Date.now() + expiresIn * 1000,
    scope,
    user,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
}

export const logout = () => {
  clearStoredAuth()
}

